import { lazy, Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cls from 'clsx';
import { observer } from 'mobx-react-lite';
import type { OptionProduct } from '@linktivity/ars-neo';
import { Delete } from '@linktivity/link-icons';
import { Button } from '@linktivity/link-ui';
import Img from '@link/share/components/Img';
import Price from '@link/share/components/Price';
import { useBookingStore } from '../context';
import styles from './bookingOptions.module.css';

// Opened by a click, so it loads then.
const OptionEditor = lazy(() => import('./OptionEditor'));

interface Editing {
  option: OptionProduct;
  open: boolean;
  /** A new draft each time the drawer opens. */
  session: number;
}

/**
 * The product's add-ons.
 *
 * Renders on the server: each one's picture, name and price are product data.
 * What has been picked and what is sold out show once the visitor picks, and
 * once the options' calendars have loaded.
 */
export const BookingOptions = observer(function BookingOptions() {
  const store = useBookingStore();
  const { t } = useTranslation();
  const [editing, setEditing] = useState<Editing | null>(null);
  const edit = (option: OptionProduct) =>
    setEditing(prev => ({
      option,
      open: true,
      session: (prev?.session ?? 0) + 1
    }));

  return (
    <>
      <ul className={styles.list}>
        {store.optionProducts.map(option => {
          const id = option.productContent?.id ?? '';
          const activity = option.productContent?.activity;
          const quantity =
            store.selectedOptions.find(pick => pick.productContentId === id)
              ?.displayInfo.totalQuantity ?? 0;
          const picked = quantity > 0;
          const soldOut = store.soldOutOptionIds.has(id);
          return (
            <li
              key={id}
              className={cls(
                styles.item,
                picked && styles.picked,
                soldOut && styles.soldOut
              )}
            >
              <Img
                src={activity?.imageUrls?.[0] || undefined}
                alt={activity?.title ?? ''}
                className={styles.image}
              />
              <div className={styles.info}>
                <div className={styles.title}>{activity?.title}</div>
                {picked && (
                  <div className={styles.note}>
                    {t('components.selectOption.quantity', { quantity })}
                  </div>
                )}
                <div className={styles.row}>
                  <div className={styles.price}>
                    <Price
                      amount={
                        store.showOtherCurrencies
                          ? option.displayAmount || 0
                          : option.amount || activity?.amount || 0
                      }
                      currencyCode={option.displayCurrency}
                    />
                  </div>
                  {soldOut ? (
                    <span className={styles.note}>
                      {t('components.selectOption.soldOut')}
                    </span>
                  ) : (
                    <div className={styles.actions}>
                      {picked && (
                        <button
                          type="button"
                          className={styles.remove}
                          aria-label={`${t('share.common.delete')}: ${activity?.title ?? ''}`}
                          onClick={() => store.removeOption(id)}
                        >
                          <Delete aria-hidden />
                        </button>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => edit(option)}
                      >
                        {picked
                          ? t('share.common.change')
                          : t('share.common.add')}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {editing && (
        <Suspense fallback={null}>
          <OptionEditor
            key={editing.session}
            option={editing.option}
            open={editing.open}
            onClose={() => setEditing(prev => prev && { ...prev, open: false })}
          />
        </Suspense>
      )}
    </>
  );
});
